import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Search, LayoutGrid, Rows3, Sparkles, FileText, ChevronLeft, ChevronRight,
    MapPin, ArrowUpDown, Loader2, Inbox,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/PageHeader';
import api from '@/lib/api';

const STATUSES = [
    { key: 'new', label: 'New', color: 'bg-sky-50 text-sky-700 border-sky-200' },
    { key: 'contacted', label: 'Contacted', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    { key: 'quoted', label: 'Quoted', color: 'bg-amber-50 text-amber-800 border-amber-200' },
    { key: 'won', label: 'Won', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { key: 'lost', label: 'Lost', color: 'bg-rose-50 text-rose-700 border-rose-200' },
];

const PAGE_SIZE = 12;

function statusMeta(key) {
    return STATUSES.find((s) => s.key === key) || STATUSES[0];
}

function LeadActions({ lead, onChangeStatus }) {
    const current = lead.status || 'new';
    const idx = STATUSES.findIndex((s) => s.key === current);
    const prev = idx > 0 ? STATUSES[idx - 1] : null;
    const next = idx >= 0 && idx < STATUSES.length - 1 ? STATUSES[idx + 1] : null;
    return (
        <div className="flex items-center gap-1">
            <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={!prev}
                onClick={() => prev && onChangeStatus(lead, prev.key)}
                data-testid={`pipeline-prev-${lead.property_key}`}
                title={prev ? `Move to ${prev.label}` : 'No previous status'}
            >
                <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                disabled={!next}
                onClick={() => next && onChangeStatus(lead, next.key)}
                data-testid={`pipeline-next-${lead.property_key}`}
                title={next ? `Move to ${next.label}` : 'No next status'}
            >
                <ChevronRight className="h-4 w-4" />
            </Button>
        </div>
    );
}

function KanbanCard({ lead, onChangeStatus }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md hover:-translate-y-0.5 will-change-transform" data-testid={`pipeline-card-${lead.property_key}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <Link to={`/app/leads/${encodeURIComponent(lead.property_key)}`} className="font-medium text-slate-900 truncate flex items-center gap-1 hover:text-amber-700">
                        <MapPin className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                        <span className="truncate">{lead.address || '—'}</span>
                    </Link>
                    <div className="text-xs text-slate-500 truncate">{lead.city}, {lead.state} <span className="font-mono">{lead.zip_code}</span></div>
                </div>
                <LeadActions lead={lead} onChangeStatus={onChangeStatus} />
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
                {lead.quality_band ? (
                    <Badge variant="outline" className="text-[10px] font-normal capitalize">{lead.quality_band} data</Badge>
                ) : <span />}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-600">
                            Actions
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                            <Link to={`/app/leads/${encodeURIComponent(lead.property_key)}`} data-testid={`pipeline-open-${lead.property_key}`}>
                                <Sparkles className="h-4 w-4 mr-2" /> Open quote
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link to={`/app/leads/${encodeURIComponent(lead.property_key)}`} data-testid={`pipeline-quote-${lead.property_key}`}>
                                <FileText className="h-4 w-4 mr-2" /> Convert to quote
                            </Link>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

function KanbanColumn({ status, leads, onChangeStatus }) {
    const meta = statusMeta(status);
    return (
        <div className="bg-slate-50 border border-slate-200 rounded-xl flex flex-col min-w-[260px] w-[260px] md:w-auto md:min-w-0 md:flex-1" data-testid={`pipeline-column-${status}`}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`${meta.color} border font-medium`}>{meta.label}</Badge>
                </div>
                <span className="text-xs text-slate-500 font-mono" data-testid={`pipeline-count-${status}`}>{leads.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px] max-h-[70vh] overflow-y-auto">
                {leads.length === 0 ? (
                    <div className="text-xs text-slate-400 text-center py-4">No leads</div>
                ) : leads.map((l) => (
                    <KanbanCard key={l.id || l.property_key} lead={l} onChangeStatus={onChangeStatus} />
                ))}
            </div>
        </div>
    );
}

function TableRow({ lead, onChangeStatus }) {
    return (
        <tr className="border-t hover:bg-slate-50" data-testid={`pipeline-row-${lead.property_key}`}>
            <td className="py-2 px-3">
                <Link to={`/app/leads/${encodeURIComponent(lead.property_key)}`} className="font-medium text-slate-900 hover:text-amber-700">
                    {lead.address || '—'}
                </Link>
                <div className="text-xs text-slate-500">{lead.city}, {lead.state} <span className="font-mono">{lead.zip_code}</span></div>
            </td>
            <td className="px-3">
                <Select value={lead.status || 'new'} onValueChange={(v) => onChangeStatus(lead, v)}>
                    <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`pipeline-status-select-${lead.property_key}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUSES.map((s) => (
                            <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </td>
            <td className="px-3 text-xs text-slate-500">{lead.quality_band || '—'}</td>
            <td className="px-3 text-xs text-slate-500 font-mono">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}</td>
            <td className="px-3 text-right">
                <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
                    <Link to={`/app/leads/${encodeURIComponent(lead.property_key)}`}><Sparkles className="h-3.5 w-3.5 mr-1" /> Open</Link>
                </Button>
            </td>
        </tr>
    );
}

export default function PipelinePage() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('kanban'); // 'kanban' | 'table'
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [page, setPage] = useState(1);

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get('/leads/unlocked');
            setLeads(r.data.items || []);
        } catch (e) {
            toast.error('Failed to load pipeline');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        let arr = leads;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            arr = arr.filter((l) => (
                (l.address || '').toLowerCase().includes(q) ||
                (l.city || '').toLowerCase().includes(q) ||
                (l.state || '').toLowerCase().includes(q) ||
                (l.zip_code || '').toString().toLowerCase().includes(q)
            ));
        }
        const sorted = [...arr];
        if (sortBy === 'newest') sorted.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        if (sortBy === 'oldest') sorted.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
        if (sortBy === 'address') sorted.sort((a, b) => (a.address || '').localeCompare(b.address || ''));
        if (sortBy === 'status') {
            const order = Object.fromEntries(STATUSES.map((s, i) => [s.key, i]));
            sorted.sort((a, b) => (order[a.status] ?? 99) - (order[b.status] ?? 99));
        }
        return sorted;
    }, [leads, search, sortBy]);

    // Pagination only applies to table view; kanban shows all in columns (with internal scroll)
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageItems = view === 'table' ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : filtered;

    useEffect(() => { setPage(1); }, [search, sortBy, view]);

    const columns = useMemo(() => {
        const map = Object.fromEntries(STATUSES.map((s) => [s.key, []]));
        for (const l of filtered) {
            const key = STATUSES.find((s) => s.key === l.status) ? l.status : 'new';
            map[key].push(l);
        }
        return map;
    }, [filtered]);

    const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s.key, columns[s.key]?.length || 0])), [columns]);

    const onChangeStatus = async (lead, newStatus) => {
        if (lead.status === newStatus) return;
        // Optimistic update
        const prev = lead.status;
        setLeads((arr) => arr.map((x) => x.property_key === lead.property_key ? { ...x, status: newStatus } : x));
        try {
            await api.patch(`/leads/${encodeURIComponent(lead.property_key)}/status?status=${encodeURIComponent(newStatus)}`);
            toast.success(`Moved to ${statusMeta(newStatus).label}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Status update failed');
            // Rollback
            setLeads((arr) => arr.map((x) => x.property_key === lead.property_key ? { ...x, status: prev } : x));
        }
    };

    if (loading) {
        return (
            <>
                <PageHeader title="Pipeline" subtitle="Track every unlocked lead from first contact to close." testId="pipeline-header" />
                <div className="text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading pipeline…</div>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title="Pipeline"
                subtitle="Track every unlocked lead from first contact to close."
                testId="pipeline-header"
                actions={
                    <Tabs value={view} onValueChange={setView}>
                        <TabsList>
                            <TabsTrigger value="kanban" data-testid="pipeline-view-kanban">
                                <LayoutGrid className="h-4 w-4 mr-1" /> Kanban
                            </TabsTrigger>
                            <TabsTrigger value="table" data-testid="pipeline-view-table">
                                <Rows3 className="h-4 w-4 mr-1" /> Table
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                }
            />

            <Card className="mb-4">
                <CardContent className="pt-4 pb-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative grow min-w-[220px]">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search address, city, ZIP…"
                                className="pl-9"
                                data-testid="pipeline-search-input"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <ArrowUpDown className="h-4 w-4 text-slate-500" />
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-[160px]" data-testid="pipeline-sort-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="newest">Newest first</SelectItem>
                                    <SelectItem value="oldest">Oldest first</SelectItem>
                                    <SelectItem value="address">Address (A→Z)</SelectItem>
                                    <SelectItem value="status">By status</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="hidden md:flex items-center gap-2 ml-auto text-xs text-slate-600 flex-wrap">
                            {STATUSES.map((s) => (
                                <Badge key={s.key} variant="outline" className={`${s.color} border font-normal`} data-testid={`pipeline-summary-${s.key}`}>
                                    {s.label}: <b className="ml-1">{counts[s.key]}</b>
                                </Badge>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {filtered.length === 0 ? (
                <Card>
                    <CardContent className="py-10 text-center text-slate-500">
                        <div className="flex flex-col items-center gap-2">
                            <Inbox className="h-8 w-8 text-slate-400" />
                            <div>No unlocked leads match this view.</div>
                            <Button asChild className="mt-2 bg-slate-900 hover:bg-slate-800 text-white"><Link to="/app/leads">Find &amp; unlock leads</Link></Button>
                        </div>
                    </CardContent>
                </Card>
            ) : view === 'kanban' ? (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 overflow-x-auto pb-2" data-testid="pipeline-kanban-board">
                    {STATUSES.map((s) => (
                        <KanbanColumn key={s.key} status={s.key} leads={columns[s.key]} onChangeStatus={onChangeStatus} />
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="pt-2 pb-2 overflow-x-auto">
                        <table className="w-full text-sm" data-testid="pipeline-table">
                            <thead>
                                <tr className="text-left text-slate-500">
                                    <th className="py-2 px-3 font-medium">Address</th>
                                    <th className="px-3 font-medium">Status</th>
                                    <th className="px-3 font-medium">Quality</th>
                                    <th className="px-3 font-medium">Unlocked</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((l) => (
                                    <TableRow key={l.id || l.property_key} lead={l} onChangeStatus={onChangeStatus} />
                                ))}
                            </tbody>
                        </table>
                        {filtered.length > PAGE_SIZE && (
                            <div className="flex items-center justify-between py-3 px-3 border-t">
                                <span className="text-xs text-slate-500">
                                    Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                                </span>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="pipeline-page-prev">
                                        <ChevronLeft className="h-4 w-4" /> Prev
                                    </Button>
                                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="pipeline-page-next">
                                        Next <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </>
    );
}
